// Lightweight spreadsheet engine: A1 addressing + Excel-style formula eval
// backed by fast-formula-parser (supports =A1+B1, SUM(A1:A9), etc.).
import FormulaParser from "fast-formula-parser";

export const colToLetter = (n) => {
  // 0-based index -> A, B, ... Z, AA
  let s = "";
  n += 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

export const letterToCol = (s) => {
  let n = 0;
  for (let i = 0; i < s.length; i++) n = n * 26 + (s.charCodeAt(i) - 64);
  return n - 1; // 0-based
};

const numericOrString = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const t = String(raw).trim();
  if (t === "") return null;
  const num = Number(t.replace(/,/g, ""));
  if (!Number.isNaN(num) && /^-?[\d,]*\.?\d+%?$/.test(t)) {
    return t.endsWith("%") ? num / 100 : num;
  }
  return raw;
};

// Compute every cell value from the raw {A1: "..."} map. Returns {A1: value}.
export function computeSheet(data, rows, cols) {
  const cache = {};
  const visiting = new Set();

  const parser = new FormulaParser({
    onCell: ({ row, col }) => {
      const addr = colToLetter(col - 1) + row; // parser is 1-based
      return getVal(addr);
    },
    onRange: (ref) => {
      const arr = [];
      for (let r = ref.from.row; r <= ref.to.row; r++) {
        const line = [];
        for (let c = ref.from.col; c <= ref.to.col; c++) {
          line.push(getVal(colToLetter(c - 1) + r));
        }
        arr.push(line);
      }
      return arr;
    },
  });

  function getVal(addr) {
    if (addr in cache) return cache[addr];
    const raw = data[addr];
    if (raw === undefined || raw === null || raw === "") {
      cache[addr] = null;
      return null;
    }
    if (typeof raw === "string" && raw.trim().startsWith("=")) {
      if (visiting.has(addr)) {
        cache[addr] = "#CIRC!";
        return "#CIRC!";
      }
      visiting.add(addr);
      try {
        const m = /^([A-Z]+)(\d+)$/.exec(addr);
        const col = letterToCol(m[1]) + 1;
        const row = parseInt(m[2], 10);
        const res = parser.parse(raw.trim().slice(1), { row, col });
        const val = res && typeof res === "object" && "result" in res ? res.result : res;
        cache[addr] = val === undefined ? null : val;
      } catch (e) {
        cache[addr] = "#ERR!";
      }
      visiting.delete(addr);
      return cache[addr];
    }
    cache[addr] = numericOrString(raw);
    return cache[addr];
  }

  const out = {};
  for (let r = 1; r <= rows; r++) {
    for (let c = 0; c < cols; c++) {
      const addr = colToLetter(c) + r;
      if (data[addr] !== undefined && data[addr] !== "") out[addr] = getVal(addr);
    }
  }
  return out;
}

export const displayValue = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "#NUM!";
    return Number.isInteger(v) ? String(v) : String(Math.round(v * 1e6) / 1e6);
  }
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return String(v);
};
